import React, { useEffect, useMemo, useRef } from 'react';
import {
  createChart,
  CrosshairMode,
  LineSeries,
  IChartApi,
  ISeriesApi,
  ColorType,
} from 'lightweight-charts';

interface StrategyPnlChartProps {
  dates: string[];
  cumPnl: number[];
  height?: number | string;
}

interface Point {
  time: string;
  value: number;
}

/// Cumulative OOS PnL line chart (dark theme), modeled on BacktestResultsChart.
export const StrategyPnlChart: React.FC<StrategyPnlChartProps> = ({ dates, cumPnl, height = 360 }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'> | null>(null);

  const points = useMemo<Point[]>(() => {
    const n = Math.min(dates.length, cumPnl.length);
    const out: Point[] = [];
    for (let i = 0; i < n; i++) {
      const v = cumPnl[i];
      if (Number.isFinite(v)) out.push({ time: dates[i], value: v });
    }
    return out;
  }, [dates, cumPnl]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const chart = createChart(el, {
      layout: {
        background: { type: ColorType.Solid, color: '#0a0b0b' },
        textColor: '#e2e8f0',
      },
      grid: {
        vertLines: { color: '#1f2937' },
        horzLines: { color: '#1f2937' },
      },
      rightPriceScale: { borderColor: '#334155' },
      timeScale: { borderColor: '#334155' },
      crosshair: { mode: CrosshairMode.Normal },
      localization: { locale: 'en-US' },
    });

    const series = chart.addSeries(LineSeries, {
      color: '#22c55e',
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const resize = () => {
      const r = el.getBoundingClientRect();
      chart.applyOptions({
        width: Math.max(0, Math.floor(r.width || 600)),
        height: Math.max(0, Math.floor(r.height || 360)),
      });
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(el);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!seriesRef.current) return;
    seriesRef.current.setData(points);
    chartRef.current?.timeScale().fitContent();
  }, [points]);

  return <div ref={wrapRef} style={{ width: '100%', height }} />;
};
